import React, {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Button,
    useBreakpointValue,
    useMediaQuery,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {Link} from 'react-router-dom'

// utils/helpers
import {remCalc} from '../../../theme/_utils'

//components
import {TextTitle} from '../text-title'
import {TextTitleDescription} from '../text-title-description'
import ResponsiveImage from '../../responsive-image'
import SocialContent from './partials/social-content'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {safeBuildUrl, getGlobalLink} from '../../../utils/contentstack-utils'
import useDamImage from '../../../custom-hooks/use-dam-image'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import SanitizedHtml from '../../html-sanitizer'
import {useProducts} from '@salesforce/commerce-sdk-react'

const onServer = typeof window === 'undefined'

export const SideCard = ({
    textGroup,
    textGroupPdp,
    mobileFullSizePdp = false,
    imageUrl,
    imageGroup, // => useDamImage(imageGroup[`image_${type}`])
    imagePosition = 'right',
    button,
    type, // standard / pdp / full
    appDownloadContent,
    imageAlt, // when dam is integrated this can be removed
    backgroundColor = 'white', // white / black / grey
    hasPaddings,
    videos,
    titleAs = 'div', // if true, the title will be rendered as h2 tag
}) => {
    const videoSrc = useBreakpointValue({
        base: videos?.mobile,
        lg: videos?.desktop,
    })
    const [isExpanded, setIsExpanded] = useState(false)
    const descriptionRef = useRef(null)
    const {buildUrl, locale} = useMultiSite()

    const categoriesData = useGetCategoriesData()
    const {data: productsData} = useProducts({
        parameters: {
            ids: button?.productsfcc?.data?.length > 0 ? button.productsfcc.data[0].id : '',
            allImages: false,
        },
    })
    const {url, label, isAbsolute} = getGlobalLink({
        link: button,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })

    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    const isReversed = imagePosition === 'right'
    // const isStandard = type === 'standard' // default behavior
    const isFull = type === 'full'
    const isPdpPage = type === 'pdp'
    const hasAppDownloads = appDownloadContent?.app_buttons?.length > 0

    const damImage = useDamImage(imageGroup?.[`image_${type}`])

    // determine layout based on screen size
    const layout = useBreakpointValue({
        base: 'vertical', // default
        sm: 'vertical',
        md: isPdpPage ? 'horizontal' : 'vertical',
        lg: 'horizontal',
    })

    // work around for media query between base and md
    const [isSmallTablet] = useMediaQuery(
        `(min-width: ${remCalc(475)}) and (max-width: ${remCalc(767)})`,
    )

    const hasDetails = isPdpPage && textGroupPdp?.bodyCopy

    //ssr note: initial html in the server is of layout "vertical"
    const isHorizontal = onServer ? false : layout === 'horizontal'

    const styles = useMultiStyleConfig('SideCard', {
        backgroundColor,
        hasPaddings,
        isPdpPage,
        isFull,
        isReversed,
        isSmallTablet,
        hasDetails,
        isExpanded,
        layoutType: isHorizontal ? 'horizontal' : 'vertical',
    })

    const handleToggle = () => {
        setIsExpanded(!isExpanded)
    }

    const descriptionHeight = descriptionRef.current?.clientHeight
    const textHeight = mobileFullSizePdp
        ? 'fit-content'
        : isExpanded
        ? descriptionHeight
        : descriptionHeight / 4

    return (
        <Box
            {...styles.container}
            data-testid={`${isReversed ? 'reversed' : 'non-reversed'}-container`}
            data-ab="editorial-sidecard"
        >
            {isHorizontal ? (
                <Flex {...styles.horizontalContainer}>
                    <Flex
                        {...styles.horizontalOutterContent}
                        data-testid={`${isFull ? 'full' : 'app'}-container`}
                    >
                        {videoSrc ? (
                            <Box
                                data-testid="video"
                                {...styles.horizontalVideoContainer}
                                aspectRatio={damImage.ratio}
                                data-ab="sidecard-video"
                            >
                                <video
                                    playsInline
                                    autoPlay
                                    muted
                                    aria-hidden="true"
                                    loop
                                    style={styles.video}
                                >
                                    <source src={videoSrc} type="video/mp4" />
                                </video>
                            </Box>
                        ) : (
                            (damImage?.url || imageUrl) && (
                                <ResponsiveImage
                                    imagelg={damImage?.url || imageUrl}
                                    alt={imageAlt}
                                    {...styles.horizontalImageContainer}
                                    data-ab="sidecard-image"
                                />
                            )
                        )}

                        <Box
                            {...((damImage?.url || imageUrl || videoSrc) &&
                                styles.horizontalInnerContent)}
                            data-ab="long-description"
                        >
                            {isPdpPage ? (
                                hasDetails ? (
                                    <TextTitleDescription
                                        {...styles.textTitleDescription}
                                        title={textGroupPdp?.title}
                                        as={titleAs}
                                    >
                                        <SanitizedHtml
                                            as={Box}
                                            html={textGroupPdp?.bodyCopy}
                                            data-testid="detailed-pdp"
                                            sx={styles.sanitizedHtmlPdp}
                                        />
                                    </TextTitleDescription>
                                ) : (
                                    <TextTitleDescription
                                        {...styles.textTitleDescription}
                                        title={textGroupPdp?.title}
                                        as={titleAs}
                                    >
                                        <SanitizedHtml as={Box} html={textGroupPdp?.bodyCopy} />
                                    </TextTitleDescription>
                                )
                            ) : (
                                <>
                                    <TextTitle {...styles.textTitleLabel}>
                                        {textGroup?.label}
                                    </TextTitle>
                                    <TextTitleDescription
                                        {...styles.textTitleDescriptionStandard}
                                        title={textGroup?.title}
                                        as={titleAs}
                                    >
                                        <SanitizedHtml as={Box} html={textGroup?.bodyCopy} />
                                    </TextTitleDescription>
                                    {hasAppDownloads ? (
                                        <SocialContent
                                            styles={styles}
                                            appContent={appDownloadContent}
                                            {...styles.socialContent}
                                        />
                                    ) : (
                                        label &&
                                        url && (
                                            <Button variant={styles.buttonVariant} {...buttonProps}>
                                                {label}
                                            </Button>
                                        )
                                    )}
                                </>
                            )}
                        </Box>
                    </Flex>
                </Flex>
            ) : (
                <Flex {...styles.verticalContainer}>
                    <Flex {...styles.verticalOutterContent} data-ab="long-description">
                        {isPdpPage ? (
                            <>
                                {hasDetails ? (
                                    <>
                                        <Box
                                            data-testid={`${
                                                mobileFullSizePdp || isExpanded
                                                    ? 'expanded'
                                                    : 'hidden'
                                            }-text`}
                                            height={textHeight}
                                            {...(mobileFullSizePdp
                                                ? styles.verticalDetailedFullSizePdp
                                                : styles.verticalDetailedPdp)}
                                        >
                                            <TextTitleDescription
                                                {...styles.textTitleDescriptionPdpVertical}
                                                title={textGroupPdp?.title}
                                                as={titleAs}
                                            >
                                                {mobileFullSizePdp && (
                                                    <ResponsiveImage
                                                        imagelg={damImage?.url || imageUrl}
                                                        alt={imageAlt}
                                                        {...styles.verticalImageContainer}
                                                    />
                                                )}
                                                <SanitizedHtml
                                                    as={Box}
                                                    html={textGroupPdp?.bodyCopy}
                                                    ref={descriptionRef}
                                                    data-testid="detailed-pdp"
                                                    sx={styles.sanitizedHtmlPdp}
                                                />
                                            </TextTitleDescription>
                                        </Box>

                                        <Button
                                            variant="text-button"
                                            {...styles.toggleButton}
                                            onClick={handleToggle}
                                        >
                                            {isExpanded
                                                ? textGroupPdp?.closeCtaTitle
                                                : textGroupPdp?.expandCtaTitle}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <TextTitleDescription
                                            {...styles.textTitleDescriptionPdpVerticalSimple}
                                            title={textGroup?.title}
                                            as={titleAs}
                                        />
                                        {videoSrc ? (
                                            <Box
                                                data-testid="video"
                                                {...styles.videoContainerPdpVertical}
                                                aspectRatio={damImage.ratio}
                                            >
                                                <video
                                                    playsInline
                                                    autoPlay
                                                    muted
                                                    aria-hidden="true"
                                                    loop
                                                    style={styles.video}
                                                >
                                                    <source src={videoSrc} type="video/mp4" />
                                                </video>
                                            </Box>
                                        ) : (
                                            <ResponsiveImage
                                                imagelg={damImage?.url || imageUrl}
                                                alt={imageAlt}
                                                width="auto"
                                            />
                                        )}
                                        <TextTitleDescription
                                            {...styles.textTitleDescriptionPdpVerticalSimpleBody}
                                        >
                                            <SanitizedHtml
                                                as={Box}
                                                html={textGroup?.bodyCopy}
                                                sx={{
                                                    a: {
                                                        color: 'blue.500',
                                                        textDecoration: 'underline',
                                                        _hover: {
                                                            color: 'blue.600',
                                                        },
                                                    },
                                                }}
                                            />
                                        </TextTitleDescription>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {textGroup?.label && (
                                    <TextTitle {...styles.textTitleVerticalLabel}>
                                        {textGroup?.label}
                                    </TextTitle>
                                )}

                                <TextTitleDescription
                                    {...styles.textTitleDescriptionVertical}
                                    title={textGroup?.title}
                                    as={titleAs}
                                />

                                {videoSrc ? (
                                    <Box
                                        data-testid="video"
                                        {...styles.videoContainerVertical}
                                        aspectRatio={damImage.ratio}
                                    >
                                        <video
                                            playsInline
                                            autoPlay
                                            muted
                                            aria-hidden="true"
                                            loop
                                            style={styles.video}
                                        >
                                            <source src={videoSrc} type="video/mp4" />
                                        </video>
                                    </Box>
                                ) : (
                                    <ResponsiveImage
                                        imagelg={damImage?.url || imageUrl}
                                        alt={imageAlt}
                                        {...styles.verticalStandardImage}
                                    />
                                )}

                                <TextTitleDescription {...styles.textTitleDescriptionVerticalBody}>
                                    <SanitizedHtml
                                        as={Box}
                                        html={textGroup?.bodyCopy}
                                        sx={{
                                            a: {
                                                color: 'blue.500',
                                                textDecoration: 'underline',
                                                _hover: {
                                                    color: 'blue.600',
                                                },
                                            },
                                        }}
                                    />
                                </TextTitleDescription>

                                {hasAppDownloads ? (
                                    <SocialContent
                                        styles={styles}
                                        appContent={appDownloadContent}
                                        {...styles.socialContent}
                                    />
                                ) : (
                                    label &&
                                    url && (
                                        <Button variant={styles.buttonVariant} {...buttonProps}>
                                            {label}
                                        </Button>
                                    )
                                )}
                            </>
                        )}
                    </Flex>
                </Flex>
            )}
        </Box>
    )
}

SideCard.propTypes = {
    textGroup: PropTypes.object,
    textGroupPdp: PropTypes.object,
    mobileFullSizePdp: PropTypes.bool,
    imageUrl: PropTypes.string,
    imageGroup: PropTypes.object,
    imagePosition: PropTypes.string,
    button: PropTypes.object || null,
    type: PropTypes.string,
    appDownloadContent: PropTypes.object,
    imageAlt: PropTypes.string,
    videoSrc: PropTypes.string,
    videos: PropTypes.object,
    hasDetails: PropTypes.bool,
    backgroundColor: PropTypes.string,
    hasPaddings: PropTypes.bool,
    titleAs: PropTypes.string,
}
